import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useRef, useState } from 'react';
import { 
  Database, 
  SlidersHorizontal,
  Bot,
  UserCheck, 
  ChevronDown,
  Scale,
  Settings,
  FileSearch
} from 'lucide-react';

// Accordion Tier Component
const TierAccordion = ({ tier, color, tag, title, description, icon: Icon, capabilities }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef(null);

  return (
    <div 
      className="bg-white border border-[#D1D5DB] rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer"
      style={{ borderLeftWidth: '4px', borderLeftColor: color }}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div className="p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
            <Icon size={28} style={{ color }} />
          </div>
          <div className="flex-1">
            <span 
              className="text-xs font-semibold uppercase tracking-wider"
              style={{ color }}
            >
              {tag}
            </span>
            <h3 className="text-xl font-bold text-[#0A1628] mt-1">{title}</h3>
            <p className="text-[#6B7280] text-sm mt-2">{description}</p>
          </div>
          <div className="flex-shrink-0 pt-2">
            <ChevronDown 
              size={24} 
              className={`text-[#6B7280] transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
            />
          </div>
        </div>
      </div>
      
      {/* Expanded Capabilities */}
      <div 
        className={`overflow-hidden transition-all duration-300 ${
          isExpanded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ maxHeight: isExpanded ? `${contentRef.current?.scrollHeight ?? 0}px` : '0px' }}
      >
        <div ref={contentRef} className="px-6 pb-6 pt-2 border-t border-[#D1D5DB]">
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {capabilities.map((cap, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div 
                  className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                  style={{ backgroundColor: color }}
                />
                <div>
                  <span className="font-semibold text-[#0A1628] text-sm">{cap.title}:</span>
                  <span className="text-[#6B7280] text-sm ml-1">{cap.description}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Design Principle Tile
const PrincipleTile = ({ icon: Icon, title, body }) => {
  return (
    <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-all">
      <CardContent className="p-8 text-center">
        <div className="p-4 bg-[#1A56DB]/10 rounded-full w-fit mx-auto mb-4">
          <Icon className="text-[#1A56DB]" size={32} />
        </div>
        <h3 className="text-xl font-bold text-[#0A1628] mb-3">{title}</h3>
        <p className="text-[#6B7280] text-sm leading-relaxed">{body}</p>
      </CardContent>
    </Card>
  );
};

const Platform = () => {
  const tiers = [
    {
      tier: 1,
      color: '#0E7490',
      tag: 'Tier 1 — Foundation',
      title: 'Financial Intelligence Foundation',
      description: 'Converts every financial input into structured, machine-ready intelligence.',
      icon: Database,
      capabilities: [
        {
          title: 'Document AI Pipeline',
          description: 'ITRs, bank statements, GST, AA feeds — extracted and structured automatically.'
        },
        {
          title: '100+ Credit-Ready Features',
          description: 'Behavioural, transactional, and document-derived features for any credit model.'
        },
        {
          title: 'Entity Resolution',
          description: 'Cross-source identity matching and deduplication.'
        },
        {
          title: 'Feature Engineering Pipeline',
          description: 'Structured JSON output for downstream model consumption.'
        }
      ]
    },
    {
      tier: 2,
      color: '#4F46E5',
      tag: 'Tier 2 — Governance',
      title: 'Business Rules & Policy Engine',
      description: 'Your credit policies and compliance mandates — configurable in real time.',
      icon: SlidersHorizontal,
      capabilities: [
        {
          title: 'No-Code Policy Configuration',
          description: 'Update credit criteria, thresholds, and routing logic without engineering.'
        },
        {
          title: 'Real-Time Compliance Enforcement',
          description: 'Regulatory mandates applied at every decision step.'
        },
        {
          title: 'Audit Trail',
          description: 'Every rule applied to every case is logged and reviewable.'
        },
        {
          title: 'Multi-Jurisdiction Support',
          description: 'Policy layers for different regulatory environments.'
        }
      ]
    },
    {
      tier: 3,
      color: '#1A56DB',
      tag: 'Tier 3 — Execution',
      title: 'Agentic Co-workers',
      description: 'AI agents and co-workers that execute across every banking workflow.',
      icon: Bot,
      capabilities: [
        {
          title: 'Underwriting Co-worker',
          description: 'Reads every file, triangulates every data source, drafts every CAM.'
        },
        {
          title: 'CreditOps Agent',
          description: 'End-to-end credit operations orchestration.'
        },
        {
          title: 'KYCOps Agent',
          description: 'Identity verification and onboarding workflows.'
        },
        {
          title: 'FraudOps Agent',
          description: 'Real-time anomaly detection and fraud signals.'
        },
        {
          title: 'PaymentOps Agent',
          description: 'Payment processing and exception handling.'
        },
        {
          title: 'CollectionsOps Agent',
          description: 'Collections workflow orchestration.'
        },
        {
          title: 'ComplianceOps Agent',
          description: 'Regulatory compliance checks and reports.'
        },
        {
          title: 'Financial Intelligence Assistant',
          description: 'Consumer and SME co-pilot.'
        }
      ]
    },
    {
      tier: 4,
      color: '#B45309',
      tag: 'Tier 4 — Human Decision',
      title: 'Human Decision Layer',
      description: 'Every AI action is reviewable. Every decision is a human decision.',
      icon: UserCheck,
      capabilities: [
        {
          title: 'Decision Dashboard',
          description: 'Full view of AI recommendations with supporting evidence and source documents.'
        },
        {
          title: 'Override and Audit',
          description: 'Every recommendation can be accepted, modified, or rejected — all logged.'
        },
        {
          title: 'Explainability Interface',
          description: 'Plain-language explanation of every AI output.'
        },
        {
          title: 'Regulatory Reporting',
          description: 'One-click audit-ready reports.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Dark Navy */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-[#060D1B] via-[#0A1628] to-[#0d1f3c]">
        <div className="max-w-[1200px] mx-auto relative z-10">
        {/* <div className="text-center" data-testid="platform-header">
            <h1 className="text-h1 text-white mb-6">Architecture.</h1>
            <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto">
              Built to run the new banking stack — from raw financial data to human decision.
            </p>
          </div> */}
          <div className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-3 backdrop-blur-sm shadow-2xl shadow-black/20">
            <div className="px-4 pt-5 pb-4 text-center">
              <h2 className="text-2xl font-bold text-white mb-2">
                The Four-Tier Operating System for Banking Intelligence
              </h2>
              <p className="text-sm text-white/65 max-w-3xl mx-auto">
                From raw financial inputs to governed AI execution and human decision-making.
              </p>
            </div>
            <img
              src="/platform-architecture-visual-v2.png"
              alt="Cognifin four-tier architecture visual"
              className="w-full rounded-xl"
            />
          </div>

        </div>
      </section>

      {/* 4-Tier Architecture Accordion */}
      <section className="section-padding px-6 bg-[#EFF6FF]" data-testid="architecture-accordion">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-h2 text-[#0A1628] mb-4">Four Tiers. One Coherent Stack.</h2>
            <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto">
              Every Cognifin solution is built on the same four-tier architecture. Each layer feeds the next.
            </p>
          </div>

          <div className="space-y-4">
            {tiers.map((tier) => (
              <TierAccordion
                key={tier.tier}
                tier={tier.tier}
                color={tier.color}
                tag={tier.tag}
                title={tier.title}
                description={tier.description}
                icon={tier.icon}
                capabilities={tier.capabilities}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Three Design Principles Section */}
      <section className="section-padding px-6 bg-white" data-testid="design-principles">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-h2 text-[#0A1628] mb-4">Three Design Principles. Non-Negotiable.</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <PrincipleTile
              icon={Scale}
              title="Intelligence Serves Judgment"
              body="AI surfaces, structures, and synthesises. Your experts decide."
            />
            <PrincipleTile
              icon={Settings}
              title="Rules Are Yours"
              body="Policies are configurable by your team, not hardcoded by ours."
            />
            <PrincipleTile
              icon={FileSearch}
              title="Every Decision is Traceable"
              body="Full audit trail from data input to final decision. Built for regulators."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding px-6 bg-gradient-to-r from-[#0A1628] to-[#1A56DB]">
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 className="text-h2 text-white mb-6">
            Explore the Architecture in Action
          </h2>
          <p className="text-body-lg text-white/80 mb-8 max-w-2xl mx-auto">
            See how the four-tier stack powers real credit operations at leading financial institutions
          </p>
          <Button 
            asChild 
            size="lg" 
            className="bg-white hover:bg-white/90 text-[#0A1628] font-bold text-lg px-10 h-14 btn-hover-lift shadow-xl"
          >
            <Link to="/contact">Request a Demo</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Platform;
